import { ChevronDownIcon, EllipsisIcon, PlusIcon, UsersIcon } from 'components/icons';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from 'components/ui/context-menu';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import {
    ComponentProps,
    CSSProperties,
    Fragment,
    ReactNode,
    RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { APIUserSlice } from 'store/api/user';
import type { ListView } from 'types/listViews';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import type { ListViewsModel } from './useListViews';

/** The dot on the open view while the list has moved off it. */
const ChangedDot = () => (
    <span className="bg-secondary ms-1.5 inline-block size-1.5 rounded-full">
        <span className="sr-only">{t('views.changed')}</span>
    </span>
);

/** A shared view's name, and whose it is. */
const SharedViewLabel = ({ view }: { view: ListView }) => (
    <span className="flex flex-col">
        <span className="font-medium">{view.name}</span>
        <span className="text-muted-foreground text-xs">
            <FormattedMessage id="views.by" values={{ name: view.owner.name }} />
        </span>
    </span>
);

type Underline = { left: number; width: number; top: number; glide: boolean };

/**
 * Where the open view's underline sits in the row, which it scrolls with. It glides when another view opens, and
 * stands at once on the first paint and when the row reflows: a restored state is not a change.
 */
function useOpenViewUnderline(row: RefObject<HTMLDivElement | null>, openId: string | undefined) {
    const [underline, setUnderline] = useState<Underline | null>(null);
    const measuredFor = useRef(openId);
    // Every render: a renamed or added view moves the open one without changing which it is.
    useLayoutEffect(() => {
        const element = row.current;
        if (!element) return;
        const measure = () => {
            const view = element.querySelector('[aria-current="true"]');
            if (!view) {
                setUnderline(null);
                return;
            }
            const from = element.getBoundingClientRect();
            const box = view.getBoundingClientRect();
            const left = box.left - from.left + element.scrollLeft;
            const top = box.bottom - from.top + element.scrollTop;
            const glide = measuredFor.current !== openId;
            measuredFor.current = openId;
            setUnderline((current) =>
                current?.left === left && current.width === box.width && current.top === top
                    ? current
                    : { left, width: box.width, top, glide: current !== null && glide }
            );
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    });
    return underline;
}

/**
 * One view in the row, a button drawn as the underlined strip's tabs are: the open one in the deep green,
 * semibold and underlined. In the new design the row draws one underline that slides to the open view, and the
 * open view's colour changes on its clock, its colour alone, while a hover changes at once. The label stacks over an
 * invisible semibold twin, so the open view's bolder weight does not widen it. Not a tab: a view changes what the
 * same list shows, and there is no panel to switch.
 */
function ViewButton({
    current,
    onOpen,
    after,
    children,
    className,
    ...props
}: ComponentProps<'button'> & {
    current: boolean;
    onOpen: () => void;
    /** Rendered after the label, outside the width reservation. */
    after?: ReactNode;
}) {
    // `props` carries what a tooltip around the view hands its trigger.
    return (
        <button
            {...props}
            type="button"
            aria-current={current ? 'true' : undefined}
            // The open view is already shown: opening it again would drop the list's changes.
            onClick={(event) => {
                props.onClick?.(event);
                if (!current) onOpen();
            }}
            className={cn(
                'text-foreground-tertiary hover:text-fg hover:bg-grey-100 new:duration-0 new:aria-[current=true]:transition-[color] new:aria-[current=true]:duration-(--duration-fast) new:ease-(--ease-smooth-out) new:motion-reduce:aria-[current=true]:duration-150 new:select-none relative flex min-h-10 flex-none cursor-pointer items-center justify-center gap-1.5 rounded-t-sm border border-transparent px-3 text-sm font-medium whitespace-nowrap transition-[color,background-color] duration-150 ease-out',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1',
                'after:bg-primary after:absolute after:inset-x-0 after:-bottom-[3px] after:h-0.5 after:opacity-0 after:transition-opacity',
                'aria-[current=true]:text-primary-strong new:aria-[current=true]:after:opacity-0 aria-[current=true]:font-semibold aria-[current=true]:after:opacity-100',
                className
            )}
        >
            <span className="grid">
                <span className="col-start-1 row-start-1">{children}</span>
                <span aria-hidden className="invisible col-start-1 row-start-1 font-semibold">
                    {children}
                </span>
            </span>
            {after}
        </button>
    );
}

/** The room the ⋯ chip takes at the end of its view's label, which the chip is drawn over. */
const MENU_ROOM = <span aria-hidden className="ms-1.5 w-[22px] shrink-0" />;

/**
 * The ⋯ menu of the open saved view: everything on the viewer's own, the rest only for a shared one. A chip
 * drawn over the end of its view, in {@link MENU_ROOM}, but a sibling of it, as a button may not hold another;
 * it comes right after the view in the focus order. Right of it are the view's 12px padding and 1px border.
 */
function ViewMenu({ model, view }: { model: ListViewsModel; view: ListView }) {
    const { openModal, closeModal } = useModal();
    const confirmDelete = () =>
        openModal(
            <ConfirmationModal
                titleId="views.delete-title"
                titleValues={{ name: view.name }}
                confirmId="views.delete"
                content={t('views.delete-confirmation-text')}
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    void model.remove(view);
                }}
            />
        );
    const items: { labelId: string; onClick: () => void; destructive?: boolean }[] = [
        ...(view.canEdit
            ? [
                  { labelId: 'views.rename', onClick: () => model.rename(view) },
                  { labelId: 'views.share', onClick: () => model.share(view) },
              ]
            : []),
        { labelId: 'views.duplicate', onClick: () => model.duplicate(view) },
        { labelId: 'views.set-default', onClick: () => model.setAsDefault(view) },
        {
            labelId: model.isViewPinned(view) ? 'pins.unpin' : 'views.pin',
            onClick: () => model.togglePinView(view),
        },
        ...(view.canEdit ? [{ labelId: 'views.delete', onClick: confirmDelete, destructive: true }] : []),
    ];
    return (
        <span className="absolute end-[13px] top-1/2 flex -translate-y-1/2">
            <DropdownMenu>
                <DropdownMenuTrigger
                    aria-label={t('actions')}
                    // Drawn 22px; `hit-area` takes the target to 32px, 44px on touch, over its own open view.
                    className="bg-neutral-tint text-foreground-secondary hover:bg-border-menu hit-area relative inline-flex size-[22px] items-center justify-center rounded-sm"
                >
                    <EllipsisIcon className="size-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[220px]">
                    {items.map(({ labelId, onClick, destructive }) => (
                        <Fragment key={labelId}>
                            {destructive ? <DropdownMenuSeparator /> : null}
                            <DropdownMenuItem
                                variant={destructive ? 'destructive' : 'default'}
                                className="h-[34px]"
                                onClick={onClick}
                            >
                                <FormattedMessage id={labelId} />
                            </DropdownMenuItem>
                        </Fragment>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </span>
    );
}

/** Shared ▾: the views of the viewer's team, then those shared with them by name, each under its owner. */
function SharedMenu({ model }: { model: ListViewsModel }) {
    const { teamName } = APIUserSlice.endpoints.getUserData.useQueryState(undefined, {
        selectFromResult: ({ data }) => ({ teamName: data?.teamName ?? null }),
    });
    const teamViews = model.sharedViews.filter((view) => view.visibility === 'TEAM');
    const peopleViews = model.sharedViews.filter((view) => view.visibility !== 'TEAM');
    const group = (label: ReactNode, views: ListView[]) =>
        views.length > 0 ? (
            <DropdownMenuGroup>
                <DropdownMenuLabel>{label}</DropdownMenuLabel>
                {views.map((view) => (
                    <DropdownMenuItem key={view.id} className="min-h-12" onClick={() => model.openView(view)}>
                        <SharedViewLabel view={view} />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuGroup>
        ) : null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="text-foreground-tertiary hover:text-fg hover:bg-grey-100 flex h-10 shrink-0 items-center gap-1.5 rounded-t-sm px-2.5 font-medium">
                <UsersIcon className="size-3.5" />
                <FormattedMessage id="views.shared" />
                <span className="bg-neutral-tint text-foreground-secondary rounded-lg px-1.5 text-xs font-semibold">
                    {model.sharedViews.length}
                </span>
                <ChevronDownIcon className="size-[13px]" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80">
                {group(teamName ?? <FormattedMessage id="views.my-team" />, teamViews)}
                {teamViews.length > 0 && peopleViews.length > 0 ? <DropdownMenuSeparator /> : null}
                {group(<FormattedMessage id="views.shared-with-you" />, peopleViews)}
                <DropdownMenuSeparator />
                <p className="text-muted-foreground px-2.5 py-1.5 text-xs">
                    <FormattedMessage id="views.shared-note" />
                </p>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * A list's views, above its filter bar: the built-in ones (in place of the list's sub-tabs), the viewer's
 * own, Shared ▾ and + Save view, over one rule. The views are a group of buttons in the Tab order, the open
 * one `aria-current`, each followed by its ⋯ menu when open. A built-in view's context menu sets it as the
 * default. The views that Pin to sidebar offers, the viewer's own and the open shared one, drag onto Pinned.
 */
export default function ViewsRow({ model }: { model: ListViewsModel }) {
    const { activeId, activeView, isChanged } = model;
    const dot = (id: string) => (isChanged && id === activeId ? <ChangedDot /> : null);
    const savedViews = activeView && !activeView.canEdit ? [...model.ownViews, activeView] : model.ownViews;
    const row = useRef<HTMLDivElement>(null);
    const underline = useOpenViewUnderline(row, activeId);

    return (
        <div data-tour="list-views" className="border-divider flex items-center gap-1 border-b">
            {/* At some browser zoom factors the open view's underline ends a sub-pixel past pb-0.5, which
                overflow-x-auto alone would turn into a vertical scrollbar: overflow-y-hidden drops it. */}
            <div
                ref={row}
                role="group"
                aria-label={t('views.label')}
                className="relative flex min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden pb-0.5"
            >
                {underline ? (
                    <span
                        aria-hidden
                        className={cn(
                            'bg-primary new:block pointer-events-none absolute top-(--underline-top) left-0 hidden h-0.5 w-(--underline-width) translate-x-(--underline-left)',
                            underline.glide &&
                                'new:transition-[translate,width] new:duration-(--duration-fast) new:ease-(--ease-smooth-out) new:motion-reduce:transition-none'
                        )}
                        style={
                            {
                                '--underline-left': `${underline.left}px`,
                                '--underline-width': `${underline.width}px`,
                                '--underline-top': `${underline.top}px`,
                            } as CSSProperties
                        }
                    />
                ) : null}
                {model.builtIns.map(({ id, labelId, tooltipId }) => {
                    const button = (
                        <ViewButton current={id === activeId} onOpen={() => model.openBuiltIn(id)} after={dot(id)}>
                            <FormattedMessage id={labelId} />
                        </ViewButton>
                    );
                    return (
                        <ContextMenu key={id}>
                            <ContextMenuTrigger className="contents">
                                {tooltipId ? (
                                    <Tooltip content={<FormattedMessage id={tooltipId} />}>{button}</Tooltip>
                                ) : (
                                    button
                                )}
                            </ContextMenuTrigger>
                            <ContextMenuContent>
                                <ContextMenuItem onClick={() => model.setAsDefault(id)}>
                                    <FormattedMessage id="views.set-default" />
                                </ContextMenuItem>
                            </ContextMenuContent>
                        </ContextMenu>
                    );
                })}
                {savedViews.length > 0 ? (
                    <span aria-hidden className="bg-border-control mx-1.5 h-[18px] w-px shrink-0 self-center" />
                ) : null}
                {savedViews.map((view) => {
                    const open = activeView?.id === view.id;
                    // The view's hover wash is drawn on its wrapper too, so it stays while the pointer is on the ⋯.
                    return (
                        <div key={view.id} className="hover:bg-grey-100 relative flex flex-none rounded-t-sm">
                            <ViewButton
                                current={open}
                                onOpen={() => model.openView(view)}
                                draggable
                                data-pin={JSON.stringify(model.viewPin(view))}
                                after={
                                    <>
                                        {dot(String(view.id))}
                                        {open ? MENU_ROOM : null}
                                    </>
                                }
                            >
                                {view.name}
                            </ViewButton>
                            {open ? <ViewMenu model={model} view={view} /> : null}
                        </div>
                    );
                })}
            </div>
            {model.sharedViews.length > 0 ? <SharedMenu model={model} /> : null}
            <Button
                variant="ghost"
                className="text-foreground-tertiary hover:text-fg hover:bg-grey-100 h-10 shrink-0 gap-1 rounded-t-sm px-2.5 font-medium"
                onClick={model.saveView}
            >
                <PlusIcon className="size-3.5" />
                <FormattedMessage id="views.save-view" />
            </Button>
        </div>
    );
}
